import { useMemo, useState, type ReactElement } from 'react';
import {
  useWeeklyPolarization,
  type PolarizationWeek,
} from '@/lib/polarization';
import { MetricTooltip } from '@/components/MetricTooltip';

/**
 * PolarizationChart — barras apiladas semanales (M2.32.B).
 *
 * Cada barra representa una semana ISO y se divide en 3 segmentos:
 *   - low (verde):   Z1+Z2 (aeróbico/endurance, el target 80%).
 *   - tempo (ámbar): Z3 (tempo / sweet-spot).
 *   - hard (rosa):   Z4+ (umbral, VO2max, anaeróbico).
 *
 * Una línea horizontal punteada marca el 80% (sweet spot) sobre la barra
 * normalizada (alturas relativas al total de la semana). Los anchos se
 * escalan al número de semanas elegidas (4-26). Si una semana no tiene
 * minutos, se pinta una barra "vacía" + label gris.
 *
 * SVG inline (sin librerías) — coherente con FormFitnessChart y
 * EvolutionByDiscipline. Tooltip nativo `title` en cada barra + tooltip
 * accesible al hover (data attributes para tests).
 *
 * Decisión de altura: para 26 semanas en mobile podríamos quedarnos sin
 * espacio horizontal. Solución: gap mínimo de 2px, ancho mínimo de barra
 * de 8px, y `overflow-x-auto` en el wrapper para scrollbar horizontal en
 * pantallas estrechas.
 */

const RANGE_OPTIONS = [
  { id: 8, label: '8 sem' },
  { id: 12, label: '12 sem' },
  { id: 26, label: '26 sem' },
] as const;

const COLOR_LOW = '#10b981'; // emerald-500
const COLOR_TEMPO = '#f59e0b'; // amber-500
const COLOR_HARD = '#f43f5e'; // rose-500

const CHART_HEIGHT = 200;
const TOP_PADDING = 16;
const BOTTOM_PADDING = 28; // labels eje X
const PLOT_HEIGHT = CHART_HEIGHT - TOP_PADDING - BOTTOM_PADDING;
const TARGET_RATIO = 0.8;

interface BarLayout {
  x: number;
  width: number;
  lowHeight: number;
  tempoHeight: number;
  hardHeight: number;
  // y absolutos (low en bottom).
  lowY: number;
  tempoY: number;
  hardY: number;
  total: number;
}

/**
 * Layout: barras de altura proporcional al total de cada semana, pero
 * dentro de cada barra la HEIGHT de cada segmento es proporcional a la
 * ratio (no a minutos absolutos). Así el 80% siempre se ve en la misma
 * altura relativa para comparar entre semanas — coherente con el target.
 */
function layoutBars(weeks: PolarizationWeek[], width: number): BarLayout[] {
  const n = weeks.length;
  if (n === 0 || width <= 0) return [];
  const gap = n > 16 ? 2 : 4;
  const totalGap = gap * (n - 1);
  const barWidth = Math.max(8, (width - totalGap) / n);

  // Para que las barras sin datos queden visibles, usamos altura uniforme
  // (PLOT_HEIGHT) — el contenido (segmentos) es la información, no la altura.
  return weeks.map((w, i): BarLayout => {
    const x = i * (barWidth + gap);
    const total = w.totalMinutes;
    const ratioLow = total > 0 ? w.lowMinutes / total : 0;
    const ratioTempo = total > 0 ? w.tempoMinutes / total : 0;
    const ratioHard = total > 0 ? w.hardMinutes / total : 0;
    const lowHeight = ratioLow * PLOT_HEIGHT;
    const tempoHeight = ratioTempo * PLOT_HEIGHT;
    const hardHeight = ratioHard * PLOT_HEIGHT;
    // Apilado: hard arriba, tempo medio, low abajo.
    const baseY = TOP_PADDING + PLOT_HEIGHT;
    const lowY = baseY - lowHeight;
    const tempoY = lowY - tempoHeight;
    const hardY = tempoY - hardHeight;
    return {
      x,
      width: barWidth,
      lowHeight,
      tempoHeight,
      hardHeight,
      lowY,
      tempoY,
      hardY,
      total,
    };
  });
}

function formatWeekLabel(weekStartISO: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(weekStartISO);
  if (!m) return weekStartISO;
  return `${m[3]}/${m[2]}`;
}

export function PolarizationChart(): ReactElement {
  const [weeks, setWeeksRange] = useState<number>(12);
  const query = useWeeklyPolarization(weeks);

  const data = query.data?.weeks ?? [];

  // Ancho fijo grande para evitar reflow; envolvemos en overflow-x-auto.
  const chartWidth = useMemo(() => {
    // Ancho proporcional al número de semanas; mínimo 480.
    return Math.max(480, weeks * 36);
  }, [weeks]);

  const bars = useMemo(() => layoutBars(data, chartWidth - 16), [data, chartWidth]);

  return (
    <article
      data-testid="polarization-chart"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            <MetricTooltip metric="polarization">Polarización 80/20</MetricTooltip>
          </h3>
          <p className="text-xs text-slate-600">
            Distribución semanal:{' '}
            <MetricTooltip metric="intensityZone">Z1-Z2 (aeróbico) / Z3 (tempo) / Z4+ (alto)</MetricTooltip>.
            Target sweet-spot: 80% en zonas bajas.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Rango">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setWeeksRange(r.id)}
              aria-pressed={weeks === r.id}
              className={
                weeks === r.id
                  ? 'rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-700 focus:ring-offset-1'
                  : 'rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:ring-offset-1'
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-sm text-slate-600">
          Cargando polarización…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="text-sm text-rose-700">
          No se pudo cargar la polarización.
        </p>
      )}

      {query.data && data.every((w) => w.totalMinutes === 0) && (
        <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
          Sin datos suficientes en las últimas {weeks} semanas.
        </p>
      )}

      {query.data && data.some((w) => w.totalMinutes > 0) && (
        <>
          <div className="overflow-x-auto">
            <svg
              role="img"
              aria-label={`Gráfico de polarización de las últimas ${weeks} semanas`}
              width={chartWidth}
              height={CHART_HEIGHT}
              data-testid="polarization-svg"
            >
              {/* Línea target 80% */}
              <line
                x1={0}
                x2={chartWidth - 16}
                y1={TOP_PADDING + PLOT_HEIGHT * (1 - TARGET_RATIO)}
                y2={TOP_PADDING + PLOT_HEIGHT * (1 - TARGET_RATIO)}
                stroke="rgb(240 240 245)"
                strokeWidth={1}
                strokeDasharray="4 4"
                opacity={0.5}
                data-testid="polarization-target-line"
              />
              <text
                x={chartWidth - 24}
                y={TOP_PADDING + PLOT_HEIGHT * (1 - TARGET_RATIO) - 4}
                fontSize="10"
                fill="#475569"
                textAnchor="end"
              >
                80% target
              </text>

              {bars.map((b, i) => {
                const w = data[i]!;
                const tooltip =
                  w.totalMinutes > 0
                    ? `${formatWeekLabel(w.weekStartISO)} · ${Math.round(w.totalMinutes)}min · ratio ${Math.round((w.polarizedRatio ?? 0) * 100)}%`
                    : `${formatWeekLabel(w.weekStartISO)} · sin datos`;
                return (
                  <g
                    key={w.weekStartISO}
                    data-testid={`polarization-bar-${i}`}
                    data-week={w.weekStartISO}
                  >
                    {/* Hard (rose) — top */}
                    {b.hardHeight > 0 && (
                      <rect
                        x={b.x}
                        y={b.hardY}
                        width={b.width}
                        height={b.hardHeight}
                        fill={COLOR_HARD}
                      >
                        <title>{tooltip}</title>
                      </rect>
                    )}
                    {/* Tempo (amber) — middle */}
                    {b.tempoHeight > 0 && (
                      <rect
                        x={b.x}
                        y={b.tempoY}
                        width={b.width}
                        height={b.tempoHeight}
                        fill={COLOR_TEMPO}
                      >
                        <title>{tooltip}</title>
                      </rect>
                    )}
                    {/* Low (emerald) — bottom */}
                    {b.lowHeight > 0 && (
                      <rect
                        x={b.x}
                        y={b.lowY}
                        width={b.width}
                        height={b.lowHeight}
                        fill={COLOR_LOW}
                      >
                        <title>{tooltip}</title>
                      </rect>
                    )}
                    {/* Barra "vacía" si no hay datos para que se vea el espacio */}
                    {b.total === 0 && (
                      <rect
                        x={b.x}
                        y={TOP_PADDING}
                        width={b.width}
                        height={PLOT_HEIGHT}
                        fill="#f1f5f9"
                        stroke="#e2e8f0"
                      >
                        <title>{tooltip}</title>
                      </rect>
                    )}
                    {/* Label fecha eje X (rotado en rangos largos) */}
                    <text
                      x={b.x + b.width / 2}
                      y={CHART_HEIGHT - 8}
                      fontSize="9"
                      fill="#64748b"
                      textAnchor="middle"
                    >
                      {formatWeekLabel(w.weekStartISO)}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Leyenda */}
          <div
            className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-600"
            data-testid="polarization-legend"
          >
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: COLOR_LOW }}
                aria-hidden="true"
              />
              Aeróbico (Z1-Z2)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: COLOR_TEMPO }}
                aria-hidden="true"
              />
              Tempo (Z3)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: COLOR_HARD }}
                aria-hidden="true"
              />
              Alto (Z4+)
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-500">
              <span
                className="inline-block h-px w-4 border-t border-dashed border-slate-700"
                aria-hidden="true"
              />
              Target 80%
            </span>
          </div>
        </>
      )}
    </article>
  );
}
